<template>
  <div class="toolbar-container def-toolbar">
    <div
      class="toolbar toolbar-scrollable"
      ref="toolbarContainer"
      @mousedown="startDrag"
      @mouseup="stopDrag"
      @mouseleave="stopDrag"
      @mousemove="drag"
      @touchstart.passive="startTouchDrag"
      @touchend="stopDrag"
      @touchcancel="stopDrag"
      @touchmove.passive="touchDrag"
      @wheel="onWheel"
    >

      <div
        id="menuCategoriesWrapper"
        class="toolbar-group"
        role="group"
        :aria-label="parentGroupLabel"
      >
        <div id="menuCategories" class="text-left">
          <div class="toolbar-parent-row toolbar-row d-flex flex-row">
            <div
              v-for="(item, index) in parentItems"
              :key="'parent-' + index"
              :class="['toolbar-option', activeGroupClass && isItemActive(item, index, 'parent') ? activeGroupClass : '', 'toolbar-parent']"
            >
              <button
                :id="parentButtonId(item, index)"
                ref="parentButtons"
                type="button"
                class="toolbar-item"
                :aria-current="isCurrentItem(item, index, 'parent') ? 'true' : null"
                :aria-expanded="hasChildItems(item) ? String(item.id === expandedParentId) : null"
                @click="handleClick(index, false)"
                @keydown="onItemKeydown($event, index, 'parent')"
              >
                {{ itemLabel(item) }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="showChildrenRow"
        id="menuSubCategoriesWrapper"
        class="toolbar-group toolbar-subgroup"
        role="group"
        :aria-labelledby="expandedParentButtonId"
      >
        <div id="menuSubCategories" class="text-left">
          <div class="toolbar-child-row toolbar-row d-flex flex-row">
            <div
              v-for="(item, index) in childItemsForExpandedParent"
              :key="'child-' + index"
              :class="['toolbar-option', activeGroupClass && isItemActive(item, index, 'child') ? activeGroupClass : '', 'toolbar-child']"
            >
              <button
                :id="childButtonId(item, index)"
                ref="childButtons"
                type="button"
                class="toolbar-item"
                :aria-current="isCurrentItem(item, index, 'child') ? 'true' : null"
                @click="handleClickChild(item)"
                @keydown="onItemKeydown($event, index, 'child')"
              >
                {{ itemLabel(item) }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'Toolbar',
    props: {
      toolbarItems: {
        type: Array,
        required: true,
      },
      selectedItemId: {
        type: [String, Number],
        required: false,
      },
    },
    data() {
      return {
        activeItem: 0, // Tracks the currently active item
        isDragging: false, // Track if the user is dragging the toolbar
        startX: 0, // Starting mouse X position
        scrollLeft: 0, // Toolbar's scroll position before dragging
        activeGroupClass: 'is-active',
        expandedParentId: null, // Track which parent is expanded for children row
      };
    },
    computed: {
      parentItems() {
        return this.toolbarItems.filter(item => !item.isChild);
      },
      childItems() {
        return this.toolbarItems.filter(item => item.isChild);
      },
      childItemsForExpandedParent() {
        return this.childItems.filter(child => child.parentId === this.expandedParentId);
      },
      showChildrenRow() {
        return this.expandedParentId && this.childItemsForExpandedParent.length > 0;
      },
      parentGroupLabel() {
        return this.$t ? this.$t('field.options') : 'Options';
      },
      selectedChildItem() {
        return this.childItems.find(item => item.id === this.selectedItemId) || null;
      },
      selectedParentItem() {
        return this.parentItems.find(item => item.id === this.selectedItemId) || null;
      },
      activeParentId() {
        if (this.selectedChildItem) {
          return this.selectedChildItem.parentId;
        }

        if (this.selectedParentItem) {
          return this.selectedParentItem.id;
        }

        return this.expandedParentId;
      },
      currentParentSelectedIndex() {
        const index = this.parentItems.findIndex(item => item.id === this.activeParentId);
        return index >= 0 ? index : 0;
      },
      currentChildSelectedIndex() {
        const index = this.childItemsForExpandedParent.findIndex(item => item.id === this.selectedItemId);
        return index >= 0 ? index : 0;
      },
      expandedParentButtonId() {
        if (!this.expandedParentId) {
          return null;
        }
        const index = this.parentItems.findIndex(item => item.id === this.expandedParentId);
        if (index < 0) {
          return null;
        }
        return this.parentButtonId(this.parentItems[index], index);
      },
    },
    watch: {
      selectedItemId() {
        this.syncExpandedParent();
        this.$nextTick(() => this.scrollSelectedIntoView());
      },
      toolbarItems: {
        handler() {
          this.syncExpandedParent();
          this.$nextTick(() => this.scrollSelectedIntoView());
        },
        deep: true,
      }
    },
    mounted() {
      this.syncExpandedParent();
      // Initial scroll into view for pre-selected item
      this.$nextTick(() => this.scrollSelectedIntoView());
    },
    methods: {
      itemLabel(item) {
        return item.description || item.name || '';
      },
      parentButtonId(item, index) {
        return `toolbar-parent-${this._uid}-${item && item.id != null ? item.id : index}`;
      },
      childButtonId(item, index) {
        return `toolbar-child-${this._uid}-${item && item.id != null ? item.id : index}`;
      },
      getItemsForGroup(group) {
        return group === 'child' ? this.childItemsForExpandedParent : this.parentItems;
      },
      hasChildItems(item) {
        return this.childItems.some(child => child.parentId === item.id);
      },
      focusItemButton(index, group) {
        const refs = group === 'child' ? this.$refs.childButtons : this.$refs.parentButtons;
        const target = Array.isArray(refs) ? refs[index] : refs;
        if (target && typeof target.focus === 'function') {
          target.focus();
        }
      },
      onItemKeydown(event, index, group) {
        const items = this.getItemsForGroup(group);
        if (!items.length) {
          return;
        }

        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          this.activateItemByIndex(index, group);
          return;
        }

        let nextIndex = index;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          nextIndex = (index + 1) % items.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          nextIndex = (index - 1 + items.length) % items.length;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = items.length - 1;
        } else {
          return;
        }

        event.preventDefault();
        this.activateItemByIndex(nextIndex, group);
      },
      activateItemByIndex(index, group) {
        const items = this.getItemsForGroup(group);
        const item = items[index];
        if (!item) {
          return;
        }

        if (group === 'child') {
          this.handleClickChild(item);
        } else {
          this.handleClick(index, false);
        }

        this.$nextTick(() => this.scrollSelectedIntoView());
        this.$nextTick(() => this.focusItemButton(index, group));
      },
      syncExpandedParent() {
        const selectedChild = this.childItems.find(item => item.id === this.selectedItemId);
        if (selectedChild) {
          this.expandedParentId = selectedChild.parentId;
          return;
        }

        const selectedParent = this.parentItems.find(item => item.id === this.selectedItemId);
        if (selectedParent) {
          this.expandedParentId = selectedParent.id;
          return;
        }

        this.expandedParentId = null;
      },
      isCurrentItem(item, index, group = 'parent') {
        if (this.selectedItemId != null && this.selectedItemId !== '') {
          return item.id === this.selectedItemId;
        }

        if (group === 'child') {
          return false;
        }

        return index === 0;
      },
      isItemActive(item, index, group = 'parent') {
        if (group === 'child') {
          return this.isCurrentItem(item, index, group);
        }

        if (this.activeParentId == null) {
          return this.isCurrentItem(item, index, group);
        }

        return item.id === this.activeParentId;
      },
      handleClick(index, isChild = false) {
        const items = isChild ? this.childItemsForExpandedParent : this.parentItems;
        const item = items[index];
        if (!item) {
          return;
        }
        if (!isChild) {
          this.expandedParentId = item.id; // Expand/collapse children row for this parent
        }
        this.activeItem = index;
        this.$emit('item-click', item && item.id !== undefined ? item.id : index);
        this.$nextTick(() => this.scrollSelectedIntoView());
      },
      handleClickChild(item) {
        this.$emit('item-click', item.id);
        this.$nextTick(() => this.scrollSelectedIntoView());
      },
      // Mouse drag
      startDrag(event) {
        if (this.shouldIgnoreDragStart(event)) {
          return;
        }
        // Prevent text selection while dragging
        event.preventDefault();
        this.isDragging = true;
        this.startX = event.pageX - this.$refs.toolbarContainer.offsetLeft;
        this.scrollLeft = this.$refs.toolbarContainer.scrollLeft;
      },
      stopDrag() {
        this.isDragging = false;
      },
      drag(event) {
        if (!this.isDragging) return;
        event.preventDefault();
        const x = event.pageX - this.$refs.toolbarContainer.offsetLeft;
        const walk = (x - this.startX) * 2; // Scroll speed
        this.$refs.toolbarContainer.scrollLeft = this.scrollLeft - walk;
      },
      // Touch drag support
      startTouchDrag(event) {
        if (this.shouldIgnoreDragStart(event)) {
          return;
        }
        if (!event.touches || !event.touches.length) return;
        this.isDragging = true;
        this.startX = event.touches[0].pageX - this.$refs.toolbarContainer.offsetLeft;
        this.scrollLeft = this.$refs.toolbarContainer.scrollLeft;
      },
      shouldIgnoreDragStart(event) {
        return !!(event && event.target && typeof event.target.closest === 'function' && event.target.closest('.toolbar-item'));
      },
      touchDrag(event) {
        if (!this.isDragging || !event.touches || !event.touches.length) return;
        const x = event.touches[0].pageX - this.$refs.toolbarContainer.offsetLeft;
        const walk = (x - this.startX) * 2;
        this.$refs.toolbarContainer.scrollLeft = this.scrollLeft - walk;
      },
      onWheel(e) {
        const container = this.$refs.toolbarContainer;
        if (!container) return;
        const hasOverflow = container.scrollWidth > container.clientWidth;
        // Convert vertical wheel to horizontal scroll when overflow exists
        if (hasOverflow) {
          // Prefer vertical delta if horizontal delta is small
          const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
          container.scrollLeft += delta;
          // Prevent page from vertically scrolling when we actually scroll horizontally
          e.preventDefault();
        }
      },
      scrollSelectedIntoView() {
        // Try to find the active selected element and ensure it's visible
        const container = this.$refs.toolbarContainer;
        if (!container) return;
        const selectedEl = container.querySelector('[aria-current="true"]') || container.querySelector('.is-active');
        if (!selectedEl) return;
        const scrollTarget = selectedEl.closest('.toolbar-option') || selectedEl;
        const elLeft = scrollTarget.offsetLeft;
        const elRight = elLeft + scrollTarget.offsetWidth;
        const containerLeft = container.scrollLeft;
        const containerRight = containerLeft + container.offsetWidth;
        if (elLeft < containerLeft) {
          container.scrollTo({ left: elLeft - 16, behavior: 'smooth' });
        } else if (elRight > containerRight) {
          container.scrollTo({ left: elRight - container.offsetWidth + 16, behavior: 'smooth' });
        }
      }
    },
  };
</script>

<style scoped>
  .toolbar-group {
    display: flex;
    flex-direction: column;
  }

  .toolbar-subgroup {
    margin-top: 0.25rem;
  }

  .toolbar-option {
    flex: 0 0 auto;
  }

  .toolbar-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    background: none;
    flex-wrap: nowrap;
    width: max-content;
  }

  .toolbar-item {
    position: relative;
  }
</style>
